import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, Users, Plus, MapPin, Crown, Clock, 
  UserPlus, Loader2, Share2, Copy, Check
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const GroupRides = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [joining, setJoining] = useState(false);
  
  // Create form state
  const [selectedTrip, setSelectedTrip] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [maxRiders, setMaxRiders] = useState("10");
  const [isPublic, setIsPublic] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [myGroupsRes, publicGroupsRes, tripsRes] = await Promise.all([
        axios.get(`${API}/group-rides`),
        axios.get(`${API}/group-rides/public`),
        axios.get(`${API}/trips`)
      ]);
      setMyGroups(myGroupsRes.data.group_rides || []);
      setPublicGroups(publicGroupsRes.data.group_rides || []);
      setTrips(tripsRes.data.trips || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedTrip || !groupName.trim()) {
      toast.error("Please select a trip and enter a group name");
      return;
    }
    
    setCreating(true);
    try {
      const res = await axios.post(`${API}/group-rides`, {
        trip_id: selectedTrip,
        name: groupName,
        description: groupDescription || undefined,
        max_riders: parseInt(maxRiders),
        is_public: isPublic
      });
      
      toast.success("Group ride created!");
      setCreateModalOpen(false);
      navigate(`/group-ride/${res.data.group_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group ride");
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    if (!inviteCode.trim() || inviteCode.length !== 6) {
      toast.error("Please enter a valid 6-character invite code");
      return;
    }
    
    setJoining(true);
    try {
      const res = await axios.post(`${API}/group-rides/join/join`, {
        invite_code: inviteCode.toUpperCase()
      });
      
      toast.success("Joined group ride!");
      setJoinModalOpen(false);
      navigate(`/group-ride/${res.data.group_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join group ride");
    } finally {
      setJoining(false);
    }
  };

  const handleJoinPublic = async (groupId) => {
    try {
      await axios.post(`${API}/group-rides/${groupId}/join`, {});
      toast.success("Joined group ride!");
      navigate(`/group-ride/${groupId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-24">
      {/* Header */}
      <div className="glass sticky top-0 z-40 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-oswald text-xl uppercase tracking-wider flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Group Rides
              </h1>
              <p className="text-xs text-gray-500">Ride together, share locations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            className="h-14 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
            onClick={() => setCreateModalOpen(true)}
          >
            <Plus className="w-5 h-5 mr-2" />
            Create
          </Button>
          <Button
            variant="outline"
            className="h-14 border-cyan-500/50 text-cyan-400 font-oswald uppercase"
            onClick={() => setJoinModalOpen(true)}
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Join Code
          </Button>
        </div>

        {/* My Group Rides */}
        {myGroups.length > 0 && (
          <div>
            <h2 className="font-oswald uppercase text-sm text-gray-400 mb-3">My Group Rides</h2>
            <div className="space-y-2">
              {myGroups.map(group => (
                <div
                  key={group.group_id}
                  className="glass rounded-xl p-4 cursor-pointer hover:border-bronze/50 transition-colors"
                  onClick={() => navigate(`/group-ride/${group.group_id}`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-oswald text-lg">{group.name}</h3>
                      <p className="text-xs text-gray-500">{group.trip_name}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                      group.status === "active" ? "bg-green-500/20 text-green-400" :
                      group.status === "waiting" ? "bg-amber-500/20 text-amber-400" :
                      "bg-gray-500/20 text-gray-400"
                    }`}>
                      {group.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {group.riders?.length || 0}/{group.max_riders}
                    </span>
                    {group.leader_id === user?.user_id && (
                      <span className="flex items-center gap-1 text-amber-400">
                        <Crown className="w-3 h-3" />
                        Leader
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Public Group Rides */}
        <div>
          <h2 className="font-oswald uppercase text-sm text-gray-400 mb-3">Public Rides to Join</h2>
          {publicGroups.length === 0 ? (
            <div className="glass rounded-xl p-6 text-center">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No public rides available</p>
              <p className="text-gray-600 text-xs mt-1">Create one and invite friends!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {publicGroups.map(group => (
                <div
                  key={group.group_id}
                  className="glass rounded-xl p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-oswald text-lg">{group.name}</h3>
                      <p className="text-xs text-gray-500">{group.trip_name}</p>
                    </div>
                    <span className="text-xs text-gray-500">
                      by {group.leader_name}
                    </span>
                  </div>
                  {group.description && (
                    <p className="text-sm text-gray-400 mb-3">{group.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Users className="w-3 h-3" />
                      {group.riders?.length || 0}/{group.max_riders} riders
                    </span>
                    <Button
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700 font-oswald uppercase"
                      onClick={() => handleJoinPublic(group.group_id)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Join
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase">Create Group Ride</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-xs text-gray-400 uppercase">Select Trip</Label>
              <Select value={selectedTrip} onValueChange={setSelectedTrip}>
                <SelectTrigger className="h-12 bg-[#121212] border-white/10">
                  <SelectValue placeholder="Choose a trip..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {trips.map(trip => (
                    <SelectItem key={trip.trip_id} value={trip.trip_id}>
                      {trip.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-gray-400 uppercase">Group Name</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Weekend Warriors"
                className="h-12 bg-[#121212] border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-gray-400 uppercase">Description (optional)</Label>
              <Textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Meet at the trailhead at 9am..."
                className="bg-[#121212] border-white/10 min-h-[60px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-gray-400 uppercase">Max Riders</Label>
                <Select value={maxRiders} onValueChange={setMaxRiders}>
                  <SelectTrigger className="h-10 bg-[#121212] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {[2, 3, 4, 5, 6, 8, 10, 15, 20].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} riders</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-gray-400 uppercase">Public?</Label>
                <div className="h-10 flex items-center">
                  <Switch
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                  />
                  <span className="ml-2 text-sm text-gray-400">
                    {isPublic ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>

            <Button
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Group Ride"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Join Modal */}
      <Dialog open={joinModalOpen} onOpenChange={setJoinModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase">Join with Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-gray-400">
              Enter the 6-character invite code shared by the group leader.
            </p>
            <Input
              placeholder="ABC123"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              className="h-14 bg-[#121212] border-white/10 text-center font-mono text-2xl tracking-[0.3em]"
              maxLength={6}
            />
            <Button
              className="w-full h-12 bg-cyan-600 hover:bg-cyan-700 font-oswald uppercase"
              onClick={handleJoin}
              disabled={joining || inviteCode.length !== 6}
            >
              {joining ? <Loader2 className="w-5 h-5 animate-spin" /> : "Join Ride"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupRides;
